// src/components/vendorChat/VendorMessageArea.tsx
import { Box, Flex, Icon, Image, Text } from '@chakra-ui/react';
import InputArea from '../InputArea';
import ArrowLeftIcon from '@/assets/svg/arrowLeft.svg?react';
import { useForm } from 'react-hook-form';
import MessageItem from './MessageItem';
import { useEffect, useRef } from 'react';
import { IChat, IMessage } from '@/services/message/types';
import { vendorChatWebSocket } from '@/services/message/websocket';

interface Props {
  chat: IChat;
  chatId?: number;
  sendMessage: (content: string) => void;
  readMessage: (msgId: string) => void;
  setDisplayMessageArea?: (v: boolean) => void;
}

const VendorMessageArea = ({
  chat,
  chatId,
  sendMessage,
  readMessage,
  setDisplayMessageArea,
}: Props) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<{ content: string }>({ defaultValues: { content: '' } });
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatContainerRef.current)
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }, [chat.messages]);

  useEffect(() => {
    if (!chatId || !chat.vendor?.user?.id) return;

    const unreadUserMessages = chat.messages.filter(
      (m) => m.sender?.id !== chat.vendor.user.id && !m.is_read,
    );

    if (unreadUserMessages.length > 0) {
      unreadUserMessages.forEach((msg) => {
        readMessage(msg.id);
        vendorChatWebSocket.markMessageAsRead(chatId, msg.id);
      });
    }
  }, [chatId, chat.messages, chat.vendor?.user?.id, readMessage]);

  const onSubmit = (data: { content: string }) => {
    if (!data.content.trim()) return;
    sendMessage(data.content);
    setValue('content', '');
  };

  const isOwnMessage = (msg: IMessage) =>
    chat.vendor?.user?.id != null && msg.sender?.id === chat.vendor.user.id;

  const formatTime = (dateString: string) => {
    const d = new Date(dateString);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box>
      <Box px="24px" py="16px" borderBottom="1px" borderColor="border.default60">
        <Flex gap={2} align="center">
          <Box
            display={{ base: 'block', lg: 'none' }}
            as="button"
            onClick={() => setDisplayMessageArea?.(false)}
          >
            <Icon boxSize="24px">
              <ArrowLeftIcon />
            </Icon>
          </Box>
          <Box
            boxSize="24px"
            borderRadius="full"
            overflow="hidden"
            bg={chat.user.photo_small ? 'transparent' : '#71B58E'}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            fontWeight="semibold"
            fontFamily="primary"
            fontSize="12px"
            lineHeight="1"
          >
            {chat.user.photo_small ? (
              <Image
                src={chat.user.photo_small}
                alt={`${chat.user.first_name} ${chat.user.last_name}`}
                boxSize="100%"
                objectFit="cover"
              />
            ) : (
              <Text as="span">
                {(chat.user.first_name?.[0] ?? '') + (chat.user.last_name?.[0] ?? '')}
              </Text>
            )}
          </Box>
          <Text fontFamily="primary" fontSize="16px" fontWeight="semibold">
            {chat.user.first_name} {chat.user.last_name}
          </Text>
        </Flex>
      </Box>

      <Box
        bg="background.secondary"
        position="relative"
        h={{ base: '65vh', lg: '500px' }}
        display="flex"
        flexDir="column"
      >
        <Box flex="1" overflowY="auto" px={5} py={2} pb="60px" ref={chatContainerRef}>
          {chat.messages
            ?.filter((m) => m.content?.trim() && m.sender?.id)
            .map((msg, idx) => {
              const isOwn = isOwnMessage(msg);
              return (
                <MessageItem key={msg.id ?? idx} variant={isOwn ? 'vendor' : 'user'}>
                  <Box
                    bg={isOwn ? '#FFF0F0' : 'background.white'}
                    p="8px"
                    border="1px"
                    borderColor="#E2E8F0"
                    borderRadius={isOwn ? '15px 0px 15px 15px' : '0px 15px 15px 15px'}
                    maxW="70%"
                    w="fit-content"
                  >
                    <Text fontSize="12px" fontFamily="primary" fontWeight="400">
                      {msg.content}
                    </Text>
                    <Flex justify="end" fontSize="8px" color="text.secondary" gap={2}>
                      {isOwn && msg.is_read && <Text color="text.secondary">Read</Text>}
                      <Text>{formatTime(msg.created_at)}</Text>
                    </Flex>
                  </Box>
                </MessageItem>
              );
            })}
        </Box>

        <Box position="sticky" bottom="0" bg="background.primary" zIndex="1">
          <InputArea handleSubmit={handleSubmit(onSubmit)} control={control} errors={errors} />
        </Box>
      </Box>
    </Box>
  );
};

export default VendorMessageArea;
